import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DraftService from '../../Services/DraftService';
import { PropertyImageWithFallback } from '../Images/PropertyImageWithFallback';
import { PurpleButton } from '../Buttons/PurpleButton';
import { RedButton } from '../Buttons/RedButton';
import { WhiteButton } from '../Buttons/WhiteButton';

const DraftCard = ({ draft, onLoad, onDelete }) => {
  const navigate = useNavigate();
  
  const getStepName = (step) => {
    const steps = ['Welcome', 'Basic Info', 'Essentials', 'Photos', 'Publish'];
    return steps[step] || 'Unknown';
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  const progressPercentage = draft.progress_percentage || ((draft.current_step + 1) / 5 * 100);
  const firstImage = draft.draft_data?.metaDetails?.images?.[0] || 
                      draft.meta_data?.images?.[0];
  const buildingName = draft.draft_data?.metaDetails?.buildingName || 
                       draft.meta_data?.buildingName || 
                       'Unnamed Property';
  
  return (
    <div className="bg-white border-[2px] border-[#E3E3E3] rounded-[10px] p-4 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] hover:shadow-lg transition-shadow">
      {/* Draft Image */}
      <div className="relative w-full h-[180px] rounded-[8px] overflow-hidden mb-3">
        {firstImage ? (
          <PropertyImageWithFallback
            src={firstImage}
            alt={buildingName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
            <svg className="w-16 h-16 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
        )}
        
        {/* Progress Badge */}
        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold">
          {Math.round(progressPercentage)}%
        </div>
      </div>
      
      {/* Draft Info */}
      <div className="space-y-2">
        <h3 className="font-semibold text-base truncate">{buildingName}</h3>
        
        <div className="flex items-center gap-2 text-xs text-[#666666]">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Step: {getStepName(draft.current_step || draft.currentStep)}</span>
        </div>
        
        <div className="flex items-center gap-2 text-xs text-[#666666]">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Last saved: {formatDate(draft.last_saved_at || draft.lastSavedAt)}</span>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <div className="flex-1">
            <PurpleButton
              text="Continue"
              onClick={() => onLoad(draft.token_id)}
            />
          </div>
          <RedButton
            text="Delete"
            onClick={() => onDelete(draft.token_id)}
          />
        </div>
      </div>
    </div>
  );
};

const DraftsViewer = ({ isModal = false, onClose = null }) => {
  const navigate = useNavigate();
  const account = useSelector((state) => state.auth.account);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  
  useEffect(() => {
    if (account) {
      loadDrafts();
    }
  }, [account]);
  
  const loadDrafts = async () => {
    try {
      setLoading(true);
      const data = await DraftService.getAllDrafts(account, true);
      setDrafts(data.drafts || []);
      setSummary(data.summary || {});
    } catch (error) {
      console.error('Error loading drafts:', error);
      toast.error('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLoadDraft = (draftId) => {
    if (isModal && onClose) {
      onClose();
    }
    navigate(`/dashboard/host/mint?draft=${draftId}`);
  };
  
  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this draft? This action cannot be undone.')) {
      return;
    }
    
    try {
      await DraftService.removeDraft(draftId, account);
      toast.success('Draft deleted successfully');
      loadDrafts(); // Reload drafts
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };
  
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <svg className="w-24 h-24 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">No drafts yet</h3>
      <p className="text-gray-500 text-center max-w-md mb-6">
        Start creating a property listing and save it as a draft to continue later.
      </p>
      <PurpleButton
        text="Create New Property"
        onClick={() => {
          if (isModal && onClose) onClose();
          navigate('/dashboard/host/mint');
        }}
      />
    </div>
  );
  
  if (!account) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Please connect your wallet to view drafts.</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold">Your Drafts</h2>
          <p className="text-[#666666] text-sm mt-1">
            {drafts.length === 0 
              ? 'No saved drafts' 
              : `${drafts.length} saved ${drafts.length === 1 ? 'draft' : 'drafts'}`}
          </p>
        </div>
        
        {drafts.length > 0 && (
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[#666666]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <WhiteButton
              text="Refresh"
              onClick={loadDrafts}
            />
          </div>
        )}
      </div>
      
      {/* Summary Stats */}
      {summary && drafts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-[10px] p-4 border border-purple-200">
            <div className="text-purple-900 text-2xl font-bold">{summary.total_drafts || 0}</div>
            <div className="text-purple-700 text-sm">Total Drafts</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-[10px] p-4 border border-green-200">
            <div className="text-green-900 text-2xl font-bold">{summary.completed_drafts || 0}</div>
            <div className="text-green-700 text-sm">Nearly Complete</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-[10px] p-4 border border-blue-200">
            <div className="text-blue-900 text-2xl font-bold">{summary.in_progress_drafts || 0}</div>
            <div className="text-blue-700 text-sm">In Progress</div>
          </div>
        </div>
      )}
      
      {/* Drafts Grid */}
      {drafts.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {drafts.map((draft) => (
            <DraftCard
              key={draft.token_id}
              draft={draft}
              onLoad={handleLoadDraft}
              onDelete={handleDeleteDraft}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DraftsViewer;



